import { toJpeg } from 'html-to-image';

/** Anything carrying this attribute (the Report a problem button) is left out of the screenshot. */
export const EXCLUDE_FROM_SCREENSHOT = 'data-support-exclude';
const CAPTURE_TIMEOUT_MS = 8000;
// the server refuses more than 4 MB decoded; a data URL is ~4/3 of that, so stay well under
const MAX_DATA_URL = 5_000_000;

/**
 * The capture clones the page, and a clone starts every scrolled panel at its top. For the moment of the capture each
 * scrolled panel is set back to the top and its children are shifted up by the same amount — the page looks exactly
 * the same, and the clone copies the shift — then everything is put back. Returns the undo.
 */
export function holdScrollForCapture(root: Element = document.body): () => void {
  const undo: Array<() => void> = [];
  const scrolled = [root, ...Array.from(root.querySelectorAll('*'))]
    .filter((el): el is HTMLElement => el instanceof HTMLElement && (el.scrollTop > 0 || el.scrollLeft > 0));
  for (const el of scrolled) {
    const { scrollTop: top, scrollLeft: left } = el;
    const kids = Array.from(el.children).filter((k): k is HTMLElement => k instanceof HTMLElement);
    const before = kids.map((k) => k.style.transform);
    kids.forEach((k) => {
      const current = getComputedStyle(k).transform;
      k.style.transform = `translate(${-left}px, ${-top}px)${current && current !== 'none' ? ` ${current}` : ''}`;
    });
    el.scrollTop = 0; el.scrollLeft = 0;
    undo.push(() => { kids.forEach((k, i) => { k.style.transform = before[i]; }); el.scrollTop = top; el.scrollLeft = left; });
  }
  return () => undo.reverse().forEach((u) => u());
}

/**
 * The visible page, as the person sees it, as a JPEG data URL, or null when it cannot be captured (cross-origin
 * content, a slow font, an old browser). Nothing leaves the browser here: the caller uploads it only if the person
 * keeps "Include screenshot" checked and presses Send.
 */
export async function captureScreen(): Promise<string | null> {
  const width = window.innerWidth, height = window.innerHeight;
  const shoot = (quality: number) => toJpeg(document.body, {
    quality, width, height, pixelRatio: 1, backgroundColor: getComputedStyle(document.body).backgroundColor || '#ffffff',
    // <noscript> would render its fallback text into the image
    filter: (node) => !(node instanceof Element && (node.hasAttribute(EXCLUDE_FROM_SCREENSHOT) || node.tagName === 'NOSCRIPT')),
  });
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), CAPTURE_TIMEOUT_MS));
  const release = holdScrollForCapture();
  try {
    let shot = await Promise.race([shoot(0.8), timeout]);
    if (shot && shot.length > MAX_DATA_URL) shot = await Promise.race([shoot(0.5), timeout]);
    return shot && shot.startsWith('data:image/jpeg;base64,') && shot.length <= MAX_DATA_URL ? shot : null;
  } catch {
    return null;
  } finally {
    release();
  }
}
